import { useQuery } from '@tanstack/react-query'
import {
  UsersIcon,
  BuildingIcon,
  TrendingUpIcon,
  UserIcon,
  UserPlusIcon,
  LayoutGridIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiClient, UserAnalytics, TenantAnalytics } from '../lib/api'
import StatCard from '../components/analytics/StatCard'
import SimpleChart from '../components/analytics/SimpleChart'

export default function Dashboard() {
  const { data: userAnalytics, isLoading: userLoading } = useQuery<UserAnalytics>({
    queryKey: ['userAnalytics'],
    queryFn: apiClient.getUserAnalytics,
  })

  const { data: tenantAnalytics, isLoading: tenantLoading } = useQuery<TenantAnalytics>({
    queryKey: ['tenantAnalytics'],
    queryFn: apiClient.getTenantAnalytics,
  })

  const isLoading = userLoading || tenantLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    )
  }

  // ── User chart data ──────────────────────────────────────────────────────────

  const roleColorMap: Record<string, string> = {
    admin: 'bg-blue-600',
    manager: 'bg-gray-600',
    user: 'bg-purple-500',
  }

  const userRoleData = userAnalytics?.users_by_role
    ? Object.entries(userAnalytics.users_by_role).map(([role, count]) => ({
      label: role.charAt(0).toUpperCase() + role.slice(1),
      value: count as number,
      color: roleColorMap[role] || 'bg-gray-400',
    }))
    : []

  const totalUserStatus =
    (userAnalytics?.users_by_status.active ?? 0) +
    (userAnalytics?.users_by_status.inactive ?? 0)

  const activeCount = userAnalytics?.users_by_status.active ?? 0
  const inactiveCount = userAnalytics?.users_by_status.inactive ?? 0
  const activePct = totalUserStatus > 0 ? Math.round((activeCount / totalUserStatus) * 100) : 0
  const inactivePct = totalUserStatus > 0 ? Math.round((inactiveCount / totalUserStatus) * 100) : 0

  const userStatusData = [
    {
      label: 'Active',
      value: activeCount,
      ringColor: 'bg-green-100',
      textColor: 'text-green-800',
      percentage: activePct,
    },
    {
      label: 'Inactive',
      value: inactiveCount,
      ringColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      percentage: inactivePct,
    },
  ]

  // ── Tenant chart data ────────────────────────────────────────────────────────

  const topTenantsData = (userAnalytics?.users_by_tenant ?? [])
    .slice(0, 6)
    .map((item: { tenant: string; count: number }) => ({
      label: item.tenant,
      value: item.count,
      color: 'bg-blue-600',
    }))

  const tenantGrowthItems = (tenantAnalytics?.tenant_growth ?? []).slice(-6).reverse()

  // ── Computed values ──────────────────────────────────────────────────────────

  const avgAdminsPerTenant =
    tenantAnalytics?.total_tenants && userAnalytics?.total_users
      ? Math.round((userAnalytics.total_users / tenantAnalytics.total_tenants) * 10) / 10
      : 0

  return (
    <div className="space-y-10">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">User overview and tenant overview</p>
      </div>

      {/* ── USER OVERVIEW ────────────────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Section header */}
        <div className="flex items-center gap-4 px-6 pt-6 pb-4">
          <div className="bg-gray-900 rounded-lg p-2 flex-shrink-0">
            <LayoutGridIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Overview</h2>
            <p className="text-sm text-gray-500">
              Get a clear overview of your user base and how it's structured.
            </p>
          </div>
        </div>

        {/* Stat cards row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-6">
          <StatCard
            title="Total Users"
            value={userAnalytics?.total_users ?? 0}
            icon={UsersIcon}
            description="Registered accounts"
          />
          <StatCard
            title="Active Users"
            value={userAnalytics?.users_by_status.active ?? 0}
            icon={TrendingUpIcon}
            description="Currently Signed in"
          />
          <StatCard
            title="Admin"
            value={userAnalytics?.users_by_role.admin ?? 0}
            icon={UserIcon}
            description="System administrators"
          />
          <StatCard
            title="New Users (30d)"
            value={userAnalytics?.recent_registrations ?? 0}
            icon={UserPlusIcon}
            description="Last 30 days"
          />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-6 py-6">
          <SimpleChart title="Users by Role" data={userRoleData} type="bar" />
          <SimpleChart title="User Status Distribution" data={userStatusData} type="donut" />
        </div>
      </section>

      {/* ── TENANT OVERVIEW ──────────────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Section header */}
        <div className="flex items-center gap-4 px-6 pt-6 pb-4">
          <div className="bg-gray-900 rounded-lg p-2 flex-shrink-0">
            <BuildingIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Tenant Overview</h2>
            <p className="text-sm text-gray-500">
              Get a clear overview of your tenanat base and how it's structured.
            </p>
          </div>
        </div>

        {/* Stat cards row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-6">
          <StatCard
            title="Total tenant"
            value={tenantAnalytics?.total_tenants ?? 0}
            icon={BuildingIcon}
            description="All client organization"
          />
          <StatCard
            title="Active Tenant"
            value={tenantAnalytics?.tenants_by_status?.active ?? 0}
            icon={TrendingUpIcon}
            description="Currently operational"
          />
          <StatCard
            title="Avg. Admin/Tenant"
            value={avgAdminsPerTenant}
            icon={UserIcon}
            description="Average admin coverage"
          />
          <StatCard
            title="New Tenants (30d)"
            value={tenantAnalytics?.recent_tenants ?? 0}
            icon={UserPlusIcon}
            description="Last 30 days"
          />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-6 py-6">
          {/* Top Tenants by User — bar chart */}
          <SimpleChart title="Top Tenants by user" data={topTenantsData} type="bar" />

          {/* Tenant Growth — 6 months */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Tenants growth (6 months)
            </h3>
            <div className="space-y-1">
              {tenantGrowthItems.length > 0 ? (
                tenantGrowthItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${item.count > 0 ? 'bg-gray-800' : 'bg-gray-300'
                          }`}
                      />
                      <span
                        className={`text-sm ${item.count > 0 ? 'text-gray-800 font-medium' : 'text-gray-400'
                          }`}
                      >
                        {item.month}
                      </span>
                    </div>
                    <span
                      className={`text-sm font-semibold ${item.count > 0 ? 'text-gray-900' : 'text-gray-300'
                        }`}
                    >
                      {item.count > 0 ? `+${item.count}` : '---'}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">No growth data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Tenant Health Table */}
        <div className="px-6 pb-6 border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Tenant Health</h3>
            <Link
              to="/tenants"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-3 text-left text-sm font-semibold text-gray-900">Tenant</th>
                  <th className="pb-3 text-left text-sm font-semibold text-blue-500 pl-8">
                    Status
                  </th>
                  <th className="pb-3 text-right text-sm font-semibold text-blue-500">Admins</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(tenantAnalytics?.tenants_with_admins ?? []).slice(0, 5).map((tenant, index) => (
                  <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 text-sm text-gray-900">{tenant.name}</td>
                    <td className="py-3 pl-8">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tenant.status === 'active'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                          }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${tenant.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                            }`}
                        />
                        {tenant.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 text-right text-sm font-bold text-gray-900">
                      {tenant.admin_count}
                    </td>
                  </tr>
                ))}
                {(tenantAnalytics?.tenants_with_admins ?? []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-sm text-gray-400">
                      No tenant data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}
